import { get, post, put, del } from '../../apiClient';
import { API_ENDPOINTS } from '../../config';

const taskService = {
  /**
   * Get all tasks with filters
   */
  getTasks: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.search) queryParams.append('search', filters.search);
      if (filters.status && filters.status !== 'all') queryParams.append('status', filters.status);
      if (filters.priority && filters.priority !== 'all') queryParams.append('priority', filters.priority);
      if (filters.owner && filters.owner !== 'all') queryParams.append('owner', filters.owner);
      if (filters.ownerId && filters.ownerId !== 'all') queryParams.append('ownerId', filters.ownerId);
      if (filters.opportunityId) queryParams.append('opportunityId', filters.opportunityId);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
      if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.TASKS}?${queryParams.toString()}`
        : API_ENDPOINTS.CRM.TASKS;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          tasks: response.data?.tasks || [],
          pagination: response.data?.pagination || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve tasks',
          tasks: []
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve tasks',
        tasks: []
      };
    }
  },

  /**
   * Get task by ID
   */
  getTaskById: async (taskId) => {
    try {
      const response = await get(`${API_ENDPOINTS.CRM.TASKS}/${taskId}`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          task: response.data?.task || response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve task',
          task: null
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve task',
        task: null
      };
    }
  },

  /**
   * Create new task
   */
  createTask: async (taskData) => {
    try {
      const response = await post(API_ENDPOINTS.CRM.TASKS, taskData);

      if (response && response.responseCode === 201) {
        return {
          success: true,
          task: response.data?.task || response.data,
          message: response.responseMessage || 'Task created successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to create task',
          task: null
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to create task',
        task: null
      };
    }
  },

  /**
   * Update task
   */
  updateTask: async (taskId, taskData) => {
    try {
      const response = await put(`${API_ENDPOINTS.CRM.TASKS}/${taskId}`, taskData);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          task: response.data?.task || response.data,
          message: response.responseMessage || 'Task updated successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to update task',
          task: null
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to update task',
        task: null
      };
    }
  },

  /**
   * Delete task
   */
  deleteTask: async (taskId) => {
    try {
      const response = await del(`${API_ENDPOINTS.CRM.TASKS}/${taskId}`);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage || 'Task deleted successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete task'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to delete task'
      };
    }
  },

  /**
   * Complete task
   */
  completeTask: async (taskId) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.TASKS}/${taskId}/complete`, {});

      if (response && response.responseCode === 200) {
        return {
          success: true,
          task: response.data?.task || response.data,
          message: response.responseMessage || 'Task completed successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to complete task',
          task: null
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to complete task',
        task: null
      };
    }
  },

  /**
   * Get task statistics
   */
  getTaskStats: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.owner && filters.owner !== 'all') queryParams.append('owner', filters.owner);
      if (filters.opportunityId) queryParams.append('opportunityId', filters.opportunityId);

      const url = queryParams.toString()
        ? `${API_ENDPOINTS.CRM.TASKS}/stats?${queryParams.toString()}`
        : `${API_ENDPOINTS.CRM.TASKS}/stats`;

      const response = await get(url);

      if (response && response.responseCode === 200) {
        return {
          success: true,
          stats: response.data?.stats || {}
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to retrieve task statistics',
          stats: {}
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to retrieve task statistics',
        stats: {}
      };
    }
  },

  /**
   * Bulk delete tasks
   */
  bulkDeleteTasks: async (ids) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.TASKS}/bulk-delete`, { ids });

      if (response && response.responseCode === 200) {
        return {
          success: true,
          message: response.responseMessage || 'Tasks deleted successfully'
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to delete tasks'
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to delete tasks'
      };
    }
  },

  /**
   * Bulk upload tasks
   */
  bulkUploadTasks: async (tasks) => {
    try {
      const response = await post(`${API_ENDPOINTS.CRM.TASKS}/bulk-upload`, { tasks });

      if (response && response.responseCode === 201) {
        return {
          success: true,
          message: response.responseMessage || 'Tasks uploaded successfully',
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.responseMessage || 'Failed to upload tasks',
          data: response.data
        };
      }
    } catch (error) {
      
      return {
        success: false,
        error: error.message || 'Failed to upload tasks'
      };
    }
  }
};

export default taskService;

